<template>
  <div v-if="isLoaded && isVisible">
    <v-row wrap cols="12">
      <v-col sm="12">
        <div class="body-2">
          <quick-filter-bar
            :selectedFilterList="filters"
            @addFilterList="addFilterList"
            @removeFilterList="removeFilterList"
            v-if="!hideQuickFilters"
          ></quick-filter-bar>

          <v-row class="mt-1">
            <v-col :md="filterButtonBarColumnLayout.left" sm="12">
              <v-btn
                @click="showFilterForm"
                class="mr-2"
                color="grey lighten-4"
                data-cy="addFilterButton"
                elevation="0"
                small
              >
                <v-icon class="mr-2" color="primary" small>fas fa-plus-circle</v-icon> Add Filter
              </v-btn>

              <span class="mr-2" v-if="filters.length">
                <v-btn
                  @click="clearFilterList"
                  class="mr-5"
                  color="grey lighten-4"
                  data-cy="clearFilterListButton"
                  elevation="0"
                  small
                  ><v-icon class="mr-2" color="red" small>fas fa-times</v-icon> {{ clearFiltersButtonText }}</v-btn
                >
              </span>
              <span>
                <filter-preset-save-button
                  :filterList="formattedFilterList"
                  v-if="formattedFilterList.length"
                ></filter-preset-save-button>
                <filter-preset-load-button
                  :filterList="formattedFilterList"
                  :presetList="formattedPresetList"
                  @loadPreset="loadPreset"
                ></filter-preset-load-button>
                <v-tooltip max-width="300" right v-if="allowEditFilters">
                  <template v-slot:activator="{ on }">
                    <v-btn data-cy="filterHelpTooltip" icon small v-on="on">
                      <v-icon small color="grey">fas fa-question-circle</v-icon>
                    </v-btn>
                  </template>
                  <span>
                    Click a filter chip to edit it, or × to remove it. Loading a preset copies its filters here — any
                    changes you make won't affect the original preset. To save your edited filters as a new preset, click
                    Save Filters.
                  </span>
                </v-tooltip>
              </span>
            </v-col>
            <v-col v-if="!hideFilterOverrideBar" :md="filterButtonBarColumnLayout.right" sm="12" class="text-right">
              <filter-override-bar @refreshList="refreshList" v-if="!isOchinUser"></filter-override-bar>
            </v-col>
          </v-row>

          <v-alert
            border="left"
            class="mb-0 mt-3"
            color="grey lighten-4"
            dense
            transition="slide-y-transition"
            v-show="filterFormVisible"
          >
            <v-row wrap class="pl-3">
              <v-col sm="12" md="3">
                <v-autocomplete
                  :items="searchableFields"
                  :menu-props="{ contentClass: 'filterSelector--field-dropdown' }"
                  @change="clearFilterOnFieldChange"
                  data-cy="filterSelector--field"
                  dense
                  filled
                  hide-details
                  item-text="name"
                  label="Field"
                  v-model="filterField"
                ></v-autocomplete>
              </v-col>
              <v-col sm="12" md="2">
                <v-autocomplete
                  :items="operations"
                  :menu-props="{ contentClass: 'filterSelector--operator-dropdown' }"
                  @change="clearFilterValue"
                  data-cy="filterSelector--operator"
                  dense
                  filled
                  hide-details
                  item-text="name"
                  item-value="value"
                  label="Operation"
                  v-model="filterOperator"
                ></v-autocomplete>
              </v-col>
              <v-col sm="12" md="5">
                <div v-if="currentFilterConflict">
                  <v-alert dense outlined type="error">
                    Conflict! There is already a filter applied that uses the same field and operator.
                  </v-alert>
                </div>
                <div
                  v-else-if="
                    filterFieldObject.valueList && filterOperator && filterFieldObject.dataType === 'templateType'
                  "
                >
                  <v-autocomplete
                    :items="templateTypeList"
                    :multiple="filterOperator === 'in' || filterOperator === 'notIn'"
                    data-cy="filterSelector_value"
                    chips
                    deletable-chips
                    dense
                    filled
                    hide-details
                    item-text="name"
                    item-value="value"
                    label="Value"
                    small-chips
                    v-model="filterValue"
                  ></v-autocomplete>
                </div>
                <div
                  v-else-if="
                    filterFieldObject.valueList && filterOperator !== 'contains' && filterOperator !== 'excludes'
                  "
                >
                  <v-autocomplete
                    :items="getHardCodedDropdownList(filterFieldObject.valueList)"
                    :multiple="filterOperator === 'in' || filterOperator === 'notIn'"
                    data-cy="filterSelector_value"
                    chips
                    deletable-chips
                    dense
                    filled
                    hide-details
                    label="Value"
                    small-chips
                    v-model="filterValue"
                  ></v-autocomplete>
                </div>
                <div v-else-if="filterFieldObject.dataType === 'dynamicDropdown'">
                  <dynamic-dropdown :attributeName="filterFieldObject.value" v-model="filterValue"></dynamic-dropdown>
                </div>
                <div v-else-if="filterFieldObject.dataType === 'pack'">
                  <div class="grey lighten-3 packSelector">
                    <v-autocomplete
                      :items="packList"
                      :multiple="filterOperator === 'in' || filterOperator === 'notIn'"
                      data-cy="filterSelector_value"
                      chips
                      deletable-chips
                      dense
                      filled
                      hide-details
                      item-text="title"
                      item-value="id"
                      label="Value"
                      small-chips
                      v-model="filterValue"
                    ></v-autocomplete>
                  </div>
                </div>
                <div v-else-if="filterFieldObject.dataType === 'user'">
                  <div class="grey lighten-3">
                    <user-picker
                      attributeName="filterPicker-value"
                      :chips="true"
                      :dense="true"
                      :filled="true"
                      :multiple="true"
                      :unAssignedIncluded="true"
                      :value="filterValue"
                      @input="selectUser"
                      hide-details
                    ></user-picker>
                  </div>
                </div>
                <div v-else-if="filterFieldObject.dataType === 'serviceAreaAttribute'">
                  <div class="grey lighten-3">
                    <v-autocomplete
                      :items="serviceAreaAttributeList"
                      :multiple="filterOperator === 'in' || filterOperator === 'notIn'"
                      :search-input.sync="searchServiceAreaAttribute"
                      data-cy="filterPicker--serviceAreaAttribute"
                      chips
                      deletable-chips
                      dense
                      filled
                      hide-details
                      item-text="name"
                      item-value="id"
                      label="Value"
                      small-chips
                      v-model="filterValue"
                    >
                    </v-autocomplete>
                  </div>
                </div>
                <div v-else-if="filterFieldObject.dataType === 'dateString'">
                  <v-date-picker
                    color="primary"
                    data-cy="filterDatePicker--value"
                    elevation="1"
                    hide-details
                    light
                    v-model="filterValue"
                  ></v-date-picker>
                </div>
                <div v-else-if="filterFieldObject.dataType === 'integerBoolean'">
                  <v-select
                    :items="[{ value: 1, label: 'Yes' }, { value: 0, label: 'No' }]"
                    data-cy="filterSelector--integerBoolean"
                    dense
                    filled
                    hide-details
                    item-text="label"
                    item-value="value"
                    label="Value"
                    v-model="filterValue"
                  ></v-select>
                </div>
                <div v-else>
                  <v-text-field
                    data-cy="filterTextField--value"
                    dense
                    filled
                    hide-details
                    label="Value"
                    v-model="filterValue"
                  ></v-text-field>
                </div>
              </v-col>
              <v-col sm="12" md="2" align-self="center">
                <v-btn
                  :disabled="disableFilterButton"
                  @click="addFilter"
                  class="mr-2"
                  color="primary"
                  data-cy="filterButton--create"
                  elevation="0"
                  small
                  >{{ editingFilterIndex !== null ? "Update Filter" : "Add Filter" }}</v-btn
                >
                <v-btn @click="hideForm" color="grey darken-2" data-cy="filterButton--cancel" outlined small
                  >Cancel</v-btn
                >
              </v-col>
            </v-row>
          </v-alert>
          <v-alert
            class="mb-0 mt-3"
            type="info"
            transition="slide-y-transition"
            dense
            v-if="hasUnavailableFilterValues"
            outlined
            role="alert"
          >
            Some filter values no longer exist in Continuum and are shown as <strong>{{ unavailableLabel }}</strong>. Those
            values will be skipped during matching, but any other values in the same filter still apply.
          </v-alert>
        </div>
      </v-col>
    </v-row>

    <div v-if="filters.length">
      <v-card outlined class="mb-5">
        <v-card-text class="py-2">
          <p class="caption grey--text text--darken-3 mb-2">
            <template v-if="allowEditFilters && editingFilterIndex !== null">Editing highlighted filter — adjust values above, then click Update Filter.</template>
            <template v-else-if="allowEditFilters">Click a filter to edit it, or × to remove it.</template>
            <template v-else>Click × to remove a filter.</template>
          </p>
          <div class="d-flex flex-wrap">
          <span v-for="(filter, index) in formattedFilterList" :key="index">
            <v-chip
              v-on="allowEditFilters ? { click: () => editFilter(index) } : {}"
              class="mr-1 my-1"
              style="max-width: 100%; height: auto; white-space: normal;"
              :color="allowEditFilters && editingFilterIndex === index ? 'warning' : 'primary'"
              data-cy="filterChip"
              label
              small
              text-color="white"
            >
              <i v-if="allowEditFilters && editingFilterIndex === index" class="fas fa-pencil-alt mr-2" style="font-size: 10px;"></i>
              {{ getFilterLabel(filter) }}
              <v-btn
                @click.stop="removeFilter(index)"
                aria-label="Remove filter"
                class="ml-2"
                color="white"
                data-cy="filterChip--remove"
                icon
                x-small
              ><v-icon x-small>fas fa-times-circle</v-icon></v-btn>
            </v-chip>
            <v-chip
              class="ml-1 mr-2 my-1"
              color="transparent"
              label
              small
              v-if="index < formattedFilterList.length - 1"
            >
              <b>AND</b>
            </v-chip>
          </span>
          </div>
        </v-card-text>
      </v-card>
    </div>
    <div class="mb-5" v-else>
      <v-alert dense color="grey lighten-2" outlined>
        <div class="py-1 grey--text">There are currently no filters applied.</div>
      </v-alert>
    </div>
  </div>
</template>

<script>
import { cloneDeep, differenceWith, filter, isEmpty, isEqual, sortBy, uniqWith } from "lodash";
import { mapActions, mapGetters, mapState } from "vuex";

const UNAVAILABLE_LABEL = "[unavailable]";

import dropdownValues from "@/store/staticDropdowns";
import DynamicDropdown from "@/components/DynamicDropdown";
import FilterOverrideBar from "@/components/FilterOverrideBar";
import FilterPresetLoadButton from "@/components/FilterPresetLoadButton";
import FilterPresetSaveButton from "@/components/FilterPresetSaveButton";
import QuickFilter from "@/components/QuickFilter";
import QuickFilterBar from "@/components/QuickFilterBar";
import UserPicker from "@/components/userPicker";

import { getFilterLabel } from "@/lib/formatters";

export default {
  name: "filterBar",
  components: {
    DynamicDropdown,
    FilterOverrideBar,
    FilterPresetLoadButton,
    FilterPresetSaveButton,
    QuickFilter,
    QuickFilterBar,
    UserPicker,
  },
  computed: {
    allSearchableFields() {
      return this.fullSearchableFieldList;
    },
    clearFiltersButtonText() {
      const filterLength = this.filters.length;
      return `Clear ${filterLength} Filter${filterLength !== 1 ? "s" : ""}`;
    },
    currentFilterConflict() {
      if (!this.filterField || !this.filterOperator) return false;
      return this.filters.some(
        (filter, idx) =>
          idx !== this.editingFilterIndex &&
          filter.attributeName === this.filterField &&
          filter.operator === this.filterOperator
      );
    },
    disableFilterButton() {
      if (this.currentFilterConflict) return true;
      const invalidFilterValue =
        this.filterValue === null ||
        this.filterValue === undefined ||
        this.filterValue === "" ||
        (Array.isArray(this.filterValue) && this.filterValue.length === 0);
      if (this.filterField && this.filterOperator && !invalidFilterValue) {
        if (this.editingFilterIndex !== null && this.editingFilterOriginal) {
          const orig = this.editingFilterOriginal;
          return (
            orig.attributeName === this.filterField &&
            orig.operator === this.filterOperator &&
            isEqual(orig.value, this.filterValue)
          );
        }
        return false;
      }
      return true;
    },
    dropdowns() {
      // TODO: When the historicallyNotImpacted feature flag is removed, this whole block `if` should be removed because there will be no need to filter.
      if (!this.historicallyNotImpactedFeatureFlag) {
        const clonedDropdownValues = cloneDeep(dropdownValues);
        const filteredTestingStatusOptions = clonedDropdownValues.testingStatus.filter(
          item => item.value !== "Historically Not Impacted"
        );
        clonedDropdownValues.testingStatus = filteredTestingStatusOptions;
        return clonedDropdownValues;
      }
      return dropdownValues;
    },
    filterButtonBarColumnLayout() {
      if (this.hideFilterOverrideBar) {
        return {
          left: "12",
          right: "0",
        };
      }
      return {
        left: "8",
        right: "4",
      };
    },
    filterFieldObject() {
      if (!this.filterField) return { valueList: null };

      for (let field of this.searchableFields) {
        if (field.value === this.filterField) return field;
      }

      return { valueList: null };
    },
    formattedFilterList() {
      if (isEmpty(this.filters)) {
        return [];
      }
      return this.formatFilterList(this.filters);
    },
    hasUnavailableFilterValues() {
      return this.formattedFilterList.some(filter => filter.valueLabel?.includes(UNAVAILABLE_LABEL));
    },
    formattedPresetList() {
      if (isEmpty(this.presetList)) {
        return [];
      }
      return this.presetList.map(preset => {
        preset.filterList = preset.filterList.map(filter => {
          return Object.assign({}, filter, {
            attributeNameLabel: this.getFilterFieldLabel(filter.attributeName),
            operatorLabel: this.getOperatorLabel(filter.operator),
            valueLabel: this.getFilterValueLabel(filter),
          });
        });
        return preset;
      });
    },
    isGlobalSearch() {
      if (!Array.isArray(this.searchableContexts)) {
        return false;
      }
      return this.searchableContexts.includes("globalSearchFields");
    },
    isLoadingUserList() {
      return this.$store.state.user.isLoadingUserList;
    },
    operations() {
      const field = this.filterFieldObject || {};
      if (!field.operations) {
        return [];
      }

      const operators = [
        {
          name: "Comma Separated List",
          value: "csv",
        },
        {
          name: "Contains",
          value: "contains",
        },
        {
          name: "Does Not Contain",
          value: "excludes",
        },
        {
          name: "Equals",
          value: "=",
        },
        {
          name: "Equals",
          value: "on",
        },
        {
          name: "Greater Than",
          value: ">",
        },
        {
          name: "Greater Than or Equal To",
          value: ">=",
        },
        {
          name: "Less Than",
          value: "<",
        },
        {
          name: "Less Than or Equal To",
          value: "<=",
        },
        {
          name: "Includes",
          value: "in",
        },
        {
          name: "Excludes",
          value: "notIn",
        },
      ];

      const results = filter(operators, op => {
        return field.operations.includes(op.value);
      });

      if (results.length === 1) {
        this.filterOperator = results[0].value;
      }

      return results;
    },
    historicallyNotImpactedFeatureFlag() {
      return this.featureFlagMap.historicallyNotImpacted;
    },
    searchableFields() {
      const fields = this.getSearchableFieldList(this.searchableContexts);
      return sortBy(fields, ["name"]);
    },
    templateTypeList() {
      const templates = Object.keys(this.templateTypeMap);
      return templates.map(item => ({
        value: item,
        name: this.$store.state.templates.list[item].name,
      }));
    },
    ...mapGetters({
      featureFlagMap: "featureFlag/getFlagMap",
      getPackById: "pack/getById",
      getSearchableFieldList: "searchableField/getAggregateListByContexts",
      getServiceAreaAttribute: "admin/serviceAreaAttribute/getById",
    }),
    ...mapState({
      dropdownList: state => state.admin.dropdown.list,
      fullSearchableFieldList: state => state.searchableField.list,
      isOchinUser: state => state.session.user.isOchinUser,
      packList: state => state.pack.packList,
      presetList: state => state.filterPreset.list,
      serviceAreaAttributeList: state => state.admin.serviceAreaAttribute.list,
      templateTypeMap: state => state.templates.list,
      userList: state => state.user.userList,
    }),
  },
  async created() {
    try {
      await Promise.all([
        this.fetchDropdownList(),
        this.fetchFeatureFlagMap(),
        this.fetchFilterPresetList(),
        this.fetchServiceAreaAttributeList(),
        this.fetchUserList(),
      ]);
      this.isLoaded = true;
    } catch (e) {
      console.error(e);
      this.isLoaded = true;
    }
  },
  data() {
    return {
      editingFilterIndex: null,
      editingFilterOriginal: null,
      filterField: {},
      filterFormVisible: false,
      filterOperator: null,
      filterValue: null,
      getFilterLabel,
      isLoaded: false,
      searchServiceAreaAttribute: null,
      filters: [],
      unavailableLabel: UNAVAILABLE_LABEL,
    };
  },
  methods: {
    addFilter() {
      const filters = this.filters;
      const filterField = this.filterField;
      const filterFieldObject = this.filterFieldObject;
      const filterOperator = this.filterOperator;
      let filterValue = this.filterValue;

      // Clear form fields before building the filter so currentFilterConflict
      // returns false during the render that closes the form, preventing a
      // brief conflict-alert flash.
      this.resetForm();

      if (filterOperator === "csv") {
        if (filterValue.charAt(filterValue.length - 1) === ",") {
          filterValue = filterValue.slice(0, -1);
        }
      }

      let tempFilter = {
        attributeName: filterField,
        dataType: filterFieldObject.dataType,
        operator: filterOperator,
        value: filterValue,
        valueType: "single",
      };

      if (Array.isArray(filterValue)) {
        tempFilter.valueType = "array";
      }

      let resultFilters;
      if (this.editingFilterIndex !== null) {
        filters.splice(this.editingFilterIndex, 1, tempFilter);
        this.editingFilterIndex = null;
        this.editingFilterOriginal = null;
        resultFilters = uniqWith(filters, isEqual);
      } else {
        filters.push(tempFilter);
        resultFilters = uniqWith(filters, isEqual);
      }

      this.filterFormVisible = false;
      this.$emit("updateFilters", this.formatFilterList(resultFilters));
    },
    editFilter(index) {
      const f = this.filters[index];
      // Suppress @change handlers on field/operator selectors so they don't
      // wipe filterValue while we're programmatically populating the form.
      this.$_isPopulating = true;
      this.filterField = f.attributeName;
      this.filterOperator = f.operator;
      this.filterValue = cloneDeep(f.value);
      this.editingFilterIndex = index;
      this.editingFilterOriginal = cloneDeep(f);
      this.filterFormVisible = true;
      this.$nextTick(() => { this.$_isPopulating = false; });
    },
    addFilterList(filterList) {
      if (!Array.isArray(filterList)) {
        return;
      }

      const filters = this.filters;

      filterList.forEach(filter => {
        filters.push(filter);
      });

      const uniqueFilterList = uniqWith(filters, isEqual);
      this.$emit("updateFilters", this.formatFilterList(uniqueFilterList));
    },
    clearFilterList() {
      this.$emit("updateFilters", []);
    },
    clearFilterOnFieldChange() {
      if (this.$_isPopulating) return;
      this.filterOperator = null;
      this.filterValue = null;
    },
    clearFilterValue() {
      if (this.$_isPopulating) return;
      this.filterValue = null;
    },
    formatFilterList(filterList) {
      if (isEmpty(filterList)) {
        return [];
      }

      return filterList.map(filter => {
        return Object.assign({}, filter, {
          attributeNameLabel: this.getFilterFieldLabel(filter.attributeName),
          operatorLabel: this.getOperatorLabel(filter.operator),
          valueLabel: this.getFilterValueLabel(filter),
        });
      });
    },
    getDynamicDropdownValueLabel(id) {
      if (Array.isArray(id)) {
        const labelList = id.reduce((acc, curr) => {
          acc.push(this.getDynamicDropdownSingleValueLabel(curr));
          return acc;
        }, []);

        return labelList.join(" —OR— ");
      }

      return this.getDynamicDropdownSingleValueLabel(id);
    },
    getDynamicDropdownSingleValueLabel(id) {
      let dropdownValueLabel;
      let dropdownValueItem;
      const dropdownValue = this.dropdownList.find(item => {
        return item.valueList.find(value => {
          if (value.id === id) {
            dropdownValueItem = value;
            dropdownValueLabel = value.label;
            return true;
          }
        });
      });

      if (!dropdownValue) {
        return UNAVAILABLE_LABEL;
      }

      if (dropdownValue.groupDropdownId) {
        dropdownValueLabel = `${dropdownValueLabel} (${this.getDynamicDropdownSingleValueLabel(
          dropdownValueItem.groupDropdownValueId
        )})`;
      }

      return dropdownValueLabel;
    },
    getHardCodedDropdownList(valueListType) {
      if (!valueListType) {
        return [];
      }

      if (valueListType === "testingStatus") {
        return this.dropdowns.testingStatus.map(status => status.value);
      }

      return this.dropdowns[valueListType];
    },
    getFilterFieldLabel(attributeName) {
      if (!attributeName) return;

      const field = this.allSearchableFields.find(field => field.value === attributeName);
      return field?.name || attributeName;
    },
    getFilterValueLabel(filter = {}) {
      const { attributeName, dataType, operator, value, valueType } = cloneDeep(filter);

      if (operator === "csv") {
        const tempValue = value.split(`,`).map(x => +x);
        return tempValue.join(" —OR— ");
      }

      if (dataType === "integerBoolean") {
        return value ? "Yes" : "No";
      }

      if (dataType === "pack" && valueType === "array") {
        const nameList = value.map(this.getPackLabel);
        return nameList.join(" —OR— ");
      }

      if (dataType === "pack") {
        return this.getPackLabel(value);
      }

      if (dataType === "dynamicDropdown") {
        return this.getDynamicDropdownValueLabel(value);
      }

      if (dataType === "templateType") {
        return this.templateTypeMap[value].name;
      }

      if (attributeName === "serviceAreaAttributeList") {
        if (Array.isArray(value)) {
          const nameList = value.map(value => {
            const { name } = this.getServiceAreaAttribute(value);
            return name;
          });

          return nameList.join(" —OR— ");
        }
        const { name } = this.getServiceAreaAttribute(value);
        return name;
      }

      if (dataType === "user") {
        if (Array.isArray(value)) {
          const nameList = value.map(val => {
            if (val === null) {
              return "Unassigned";
            }
            const user = this.userList.find(user => user.username === val);
            if (!user) return val;
            // Return formatted name: "FirstName LastName"
            return user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.email;
          });
          return nameList.join(" —OR— ");
        }
        // Handle single null value (Unassigned)
        if (value === null) {
          return "Unassigned";
        }
        const user = this.userList.find(user => user.username === value);
        if (!user) return value;
        // Return formatted name: "FirstName LastName"
        return user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.email;
      }

      let tempValue = value;
      if (Array.isArray(value)) {
        if (attributeName === "assignedTo") {
          tempValue = value.map(val => (val === null ? "Unassigned" : val));
        }
        return tempValue.join(" —OR— ");
      }
      return `${filter.value.toString()}`;
    },
    getOperatorLabel(operator) {
      if (!operator) return;

      if (operator === "in") {
        return "=";
      }

      if (operator === "notIn") {
        return "!=";
      }

      if (operator === "excludes") {
        return "does not contain";
      }

      if (operator === "csv") {
        return "=";
      }

      return operator;
    },
    getPackLabel(packId) {
      const pack = this.getPackById(packId);
      return pack ? pack.title : packId;
    },
    hideForm() {
      this.filterFormVisible = false;
      this.editingFilterIndex = null;
      this.editingFilterOriginal = null;
      this.resetForm();
    },
    loadPreset(preset) {
      this.replaceFilterList(preset.filterList);
    },
    refreshList() {
      this.$emit("refreshList");
    },
    removeFilter(idx) {
      if (this.editingFilterIndex !== null) {
        if (idx <= this.editingFilterIndex) {
          // Removing at or before the editing slot shifts the index — cancel to avoid corrupting wrong filter
          this.hideForm();
        }
        // idx > editingFilterIndex: the editing slot is unaffected, let the edit continue
      }
      let filters = this.filters;
      filters.splice(idx, 1);
      this.$emit("updateFilters", filters);
    },
    removeFilterList(filterList) {
      if (!Array.isArray(filterList)) {
        return;
      }
      if (this.editingFilterIndex !== null) {
        this.hideForm();
      }
      const filters = this.filters;
      const resultFilterList = differenceWith(filters, filterList, isEqual);
      this.$emit("updateFilters", resultFilterList);
    },
    replaceFilterList(filterList) {
      if (!Array.isArray(filterList)) {
        return;
      }
      this.$emit("updateFilters", filterList);
    },
    resetForm() {
      this.filterField = null;
      this.filterOperator = null;
      this.filterValue = null;
    },

    selectUser(user) {
      if (this.$_isPopulating) return;
      this.filterValue = user;
    },
    showFilterForm() {
      this.hideForm();
      this.filterFormVisible = true;
    },
    showQuickFilter(filter) {
      this.filterValue = null;
      this.filterField = filter.filterField;
      this.filterOperator = filter.filterOperator;
      this.filterFormVisible = true;
    },
    ...mapActions({
      fetchDropdownList: "admin/dropdown/list",
      fetchFeatureFlagMap: "featureFlag/load",
      fetchFilterPresetList: "filterPreset/list",
      fetchServiceAreaAttributeList: "admin/serviceAreaAttribute/list",
      fetchUserList: "user/load",
    }),
  },
  watch: {
    propFilters(newVal) {
      if (this.editingFilterIndex !== null) {
        this.hideForm();
      }
      this.filters = newVal;
    },
  },
  props: {
    allowEditFilters: {
      type: Boolean,
      default: false,
    },
    propFilters: {},
    hideFilterOverrideBar: {
      type: Boolean,
      default: false,
    },
    hideQuickFilters: {
      type: Boolean,
      default: false,
    },
    isVisible: {},
    searchableContexts: {},
  },
};
</script>
